import { ApiError, NetworkError } from "../api/client";
import type { Command, CommandError, SyncResponse } from "../api/types";
import { applyCommand, remapTempIds } from "./apply";
import { emptyState, mergeSyncResponse, type SyncState } from "./state";

export interface Transport {
  sync(syncToken: string, commands: Command[]): Promise<SyncResponse>;
}

export interface Snapshot {
  confirmed: SyncState;
  pending: Command[];
  tempIds: Record<string, string>;
}

export interface Persistence {
  load(): Promise<Snapshot | null>;
  save(snapshot: Snapshot): Promise<void>;
}

export type CommandResult = { ok: true; id?: string } | { ok: false; error: CommandError };

export interface CommandFailure {
  command: Command;
  error: CommandError;
}

export type EngineStatus = "idle" | "syncing" | "offline" | "unauthorized";

export interface EngineEvents {
  onFailure?(failures: CommandFailure[]): void;
  onStatus?(status: EngineStatus): void;
}

let counter = 0;
export function newUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  counter += 1;
  return `${Date.now().toString(36)}-${counter}-${Math.random().toString(36).slice(2)}`;
}

/** Build a command with a fresh uuid (and temp_id for creating commands). */
export function command(type: string, args: Record<string, unknown>, tempId?: string): Command {
  return tempId ? { type, args, uuid: newUuid(), temp_id: tempId } : { type, args, uuid: newUuid() };
}

/**
 * Sync-based local store, mirroring the official clients:
 * - `confirmed` is the server's state as of `confirmed.syncToken`;
 * - commands are applied optimistically (`view` = confirmed + in-flight + pending, replayed);
 * - a flush sends pending commands together with an incremental read, so the answer carries the
 *   server's version of everything they touched. Failed commands simply drop out of the replay,
 *   which is the rollback; temp ids are remapped in the commands still pending.
 */
export class SyncEngine {
  private confirmed: SyncState = emptyState();
  private inflight: Command[] = [];
  private pending: Command[] = [];
  private tempIds: Record<string, string> = {};
  private view: SyncState = this.confirmed;
  private listeners = new Set<() => void>();
  private waiters = new Map<string, (r: CommandResult) => void>();
  private running: Promise<void> | null = null;
  private again = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  status: EngineStatus = "idle";

  constructor(
    private transport: Transport,
    private persistence: Persistence | null = null,
    private events: EngineEvents = {},
    private batchDelayMs = 60,
  ) {}

  get state(): SyncState {
    return this.view;
  }
  get hasData(): boolean {
    return this.confirmed.syncToken !== "*";
  }
  get pendingCount(): number {
    return this.pending.length + this.inflight.length;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Real id for an id that may be a temp id already mapped by the server. */
  resolveId(id: string): string {
    return this.tempIds[id] ?? id;
  }

  private initialized: Promise<void> | null = null;

  /** Load the persisted snapshot (once). */
  init(): Promise<void> {
    this.initialized ??= this.load();
    return this.initialized;
  }

  private async load(): Promise<void> {
    const snap = await this.persistence?.load().catch(() => null);
    if (snap) {
      this.confirmed = snap.confirmed;
      this.pending = snap.pending;
      this.tempIds = snap.tempIds;
      this.recompute();
    }
  }

  /** Queue commands, apply them locally now and flush shortly. Resolves with each command's result. */
  enqueue(commands: Command | Command[]): Promise<CommandResult[]> {
    const list = Array.isArray(commands) ? commands : [commands];
    this.pending.push(...list);
    this.recompute();
    void this.persist();
    const results = Promise.all(list.map((c) => new Promise<CommandResult>((res) => this.waiters.set(c.uuid, res))));
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.sync().catch(() => undefined);
    }, this.batchDelayMs);
    return results;
  }

  /** Send pending commands (if any) and read incremental changes. Calls are serialized. */
  sync(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      try {
        do {
          this.again = false;
          await this.flushOnce();
        } while (this.again || this.pending.length > 0);
      } finally {
        this.running = null;
      }
    })();
    return this.running;
  }

  private setStatus(status: EngineStatus) {
    if (this.status === status) return;
    this.status = status;
    this.events.onStatus?.(status);
    this.emit();
  }

  private async flushOnce(): Promise<void> {
    const batch = this.pending;
    this.pending = [];
    this.inflight = batch;
    this.setStatus("syncing");
    let res: SyncResponse;
    try {
      res = await this.transport.sync(this.confirmed.syncToken, batch);
    } catch (err) {
      this.inflight = [];
      if (err instanceof ApiError && !err.isAuth && err.status >= 400 && err.status < 500 && err.status !== 429) {
        // The whole request was rejected (malformed command): drop the batch instead of looping.
        const error: CommandError = {
          error: err.message,
          error_code: err.body?.error_code ?? 0,
          error_tag: err.tag,
          http_code: err.status,
        };
        this.settle(batch, {}, error);
        this.recompute();
        this.setStatus("idle");
        return;
      }
      this.pending = [...batch, ...this.pending];
      this.recompute();
      this.setStatus(err instanceof ApiError && err.isAuth ? "unauthorized" : "offline");
      throw err instanceof ApiError || err instanceof NetworkError ? err : new NetworkError(String(err));
    }
    this.confirmed = mergeSyncResponse(this.confirmed, res);
    const mapping = res.temp_id_mapping ?? {};
    Object.assign(this.tempIds, mapping);
    this.inflight = [];
    this.settle(batch, res.sync_status ?? {}, null, mapping);
    if (Object.keys(mapping).length) {
      this.pending = this.pending.map((c) => ({ ...c, args: remapTempIds(c.args, mapping) }));
    }
    this.recompute();
    this.setStatus("idle");
    void this.persist();
  }

  private settle(
    batch: Command[],
    status: Record<string, "ok" | CommandError>,
    requestError: CommandError | null,
    mapping: Record<string, string> = {},
  ) {
    const failures: CommandFailure[] = [];
    for (const cmd of batch) {
      const s = requestError ?? status[cmd.uuid] ?? "ok";
      const result: CommandResult =
        s === "ok"
          ? { ok: true, id: cmd.temp_id ? (mapping[cmd.temp_id] ?? cmd.temp_id) : undefined }
          : { ok: false, error: s };
      if (!result.ok) failures.push({ command: cmd, error: result.error });
      this.waiters.get(cmd.uuid)?.(result);
      this.waiters.delete(cmd.uuid);
    }
    if (failures.length) this.events.onFailure?.(failures);
  }

  private recompute() {
    let s = this.confirmed;
    for (const c of this.inflight) s = applyCommand(s, c);
    for (const c of this.pending) s = applyCommand(s, c);
    this.view = s;
    this.emit();
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  private async persist() {
    if (!this.persistence) return;
    try {
      await this.persistence.save({
        confirmed: this.confirmed,
        pending: [...this.inflight, ...this.pending],
        tempIds: this.tempIds,
      });
    } catch {
      // persistence is a cache; ignore failures
    }
  }
}
